import { campaignQueryKeys, isCampaignAnalysisTerminal } from '@/entities/campaign';
import { CAMPAIGN_ANALYSIS_STATUS } from '@/entities/campaign/generated/campaign-constants';
import type {
  CampaignAnalysisHistoryItem,
  CampaignAnalysisStatus,
} from '@/entities/campaign/generated/campaign-type';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useCampaignAnalysisStatus } from '@/features/campaign/hooks/query';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

export function useCampaignAnalysisRun(
  campaignId: string,
  selectedRun: CampaignAnalysisHistoryItem | null,
) {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const invalidatedTerminalRunIdRef = useRef<string | null>(null);
  const isRunning =
    selectedRun?.status === CAMPAIGN_ANALYSIS_STATUS.REQUESTING ||
    selectedRun?.status === CAMPAIGN_ANALYSIS_STATUS.PROCESSING;
  const statusQuery = useCampaignAnalysisStatus(campaignId, selectedRun?.run_id, {
    enabled: isRunning,
  });
  const status = (statusQuery.data?.status ??
    selectedRun?.status ??
    null) as CampaignAnalysisStatus | null;

  useEffect(() => {
    if (!selectedRun || !isCampaignAnalysisTerminal(statusQuery.data?.status)) return;
    if (invalidatedTerminalRunIdRef.current === selectedRun.run_id) return;

    invalidatedTerminalRunIdRef.current = selectedRun.run_id;

    void queryClient.invalidateQueries({
      queryKey: campaignQueryKeys.analysisList(workspaceId, campaignId),
    });
  }, [campaignId, queryClient, selectedRun, statusQuery.data?.status, workspaceId]);

  return {
    status,
    failureReason: statusQuery.data?.failure_reason ?? selectedRun?.failure_reason ?? null,
    isStatusError: isRunning && statusQuery.isError,
    refetchStatus: statusQuery.refetch,
  };
}
